/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  MenuItem,
  ListItem,
  List,
  Input,
  Select,
} from '@mui/material';
import { uneditableFields, WipTree, WipTreeElement } from '@/store/types';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/store';
import { updateMultipleDependency } from '@/store/storeSlice';
import {
  Icon,
  Input as InputFilter,
  Button,
  useDebounceCallback,
} from '@data-products-and-ai/react-components';

import { parsePropertytoLabel } from '@/utils/helpers';

interface AdditionalOption {
  [key: string]: Options[];
}

interface Options {
  label: string;
  value: string | number;
}

interface MultiEditModalProps {
  area: keyof WipTree;
  keyProp: string;
  name: string;
  additionalOptions?: AdditionalOption;
}

const MultiEditModal: React.FC<MultiEditModalProps> = ({
  area,
  keyProp,
  name,
  additionalOptions,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedElements, setSelectedElements] = useState<string[]>([]);
  const [step, setStep] = useState(1);
  const [selectedField, setSelectedField] = useState('');
  const [newValue, setNewValue] = useState('');
  const [data, setData] = useState<WipTreeElement<any>>(
    {} as WipTreeElement<any>,
  );
  const [fieldsOptions, setFieldsOptions] = useState<string[]>([]);
  const [filterQuery, setFilterQuery] = useState('');
  const [filteredData, setFilteredData] = useState<WipTreeElement<any>>(
    {} as WipTreeElement<any>,
  );
  const [currentPage, setCurrentPage] = useState<number>(0);
  const ELEMENTSPERPAGE = 100;
  const currentElements = {
    allIds:
      filteredData.allIds?.slice(
        currentPage * ELEMENTSPERPAGE,
        currentPage * ELEMENTSPERPAGE + ELEMENTSPERPAGE,
      ) ?? [],
    byId: Object.fromEntries(
      filteredData.allIds
        ?.slice(
          currentPage * ELEMENTSPERPAGE,
          currentPage * ELEMENTSPERPAGE + ELEMENTSPERPAGE,
        )
        .map((id) => [id, filteredData.byId[id]]) ?? [],
    ),
  };

  useEffect(() => {
    if (!data.allIds) return;
    /*     const startIndex = currentPage * ELEMENTSPERPAGE;
    const endIndex = startIndex + ELEMENTSPERPAGE; */
    if (filterQuery) {
      setFilteredData({
        allIds: data.allIds.filter((id) =>
          data.byId[id][name].value
            .toLowerCase()
            .includes(filterQuery.toLowerCase()),
        ),
        byId: Object.fromEntries(
          data.allIds
            .filter((id) =>
              data.byId[id][name].value
                .toLowerCase()
                .includes(filterQuery.toLowerCase()),
            )
            .map((id) => [id, data.byId[id]]),
        ),
      });
    } else {
      setFilteredData(data);
    }
  }, [filterQuery, data, currentPage]);

  useEffect(() => {
    setCurrentPage(0);
  }, [filterQuery]);

  const selectedScenario = useSelector((state: RootState) =>
    state.store.Simulation.scenarios.find((item) => item.is_selected),
  );

  useEffect(() => {
    setFilterQuery('');
  }, [step, open]);

  const dispatch = useDispatch();
  const handleInputChangeDebounced = useDebounceCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFilterQuery(event.target.value);
      console.log(event.target.value);
    },
    1000,
  );
  const handleToggle = (id: string) => {
    setSelectedElements((prev) =>
      prev.includes(id) ? prev.filter((el) => el !== id) : [...prev, id],
    );
  };

  const handleNext = () => {
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSubmit = () => {
    if (
      window.confirm(
        `Are you sure you want to change the field ${parsePropertytoLabel(
          selectedField,
        )} for ${selectedElements.length} elements?`,
      )
    ) {
      let value = newValue;
      if (selectedField.includes('DATE')) {
        const date = new Date(value);
        value = Math.floor(date.getTime() / 1000).toString();
      } else if (
        selectedField.includes('TIME') &&
        selectedField !== 'REDUCED_TIME'
      ) {
        const [hours, minutes] = value.split(':').map(Number);
        value = (hours * 3600 + minutes * 60).toString();
      }

      /* const time = hour * 3600 + minutes * 60 */
      dispatch(
        updateMultipleDependency({
          area: area,
          key: keyProp,
          content: selectedElements.map((id) => ({
            [keyProp]: { value: id },
            [selectedField]: { value: value },
          })),
        }),
      );

      setOpen(false);
      setStep(1);
      setSelectedElements([]);
      setSelectedField('');
      setNewValue('');
    }
  };

  useEffect(() => {
    if (selectedScenario) {
      setData(selectedScenario?.data[area]);
      setFilteredData(selectedScenario?.data[area]);
      if (
        selectedScenario?.data[area] &&
        selectedScenario?.data[area].allIds.length > 0
      ) {
        const value =
          selectedScenario.data[area].byId[
            selectedScenario.data[area].allIds[0]
          ];
        setFieldsOptions(
          Object.keys(value).filter(
            (key) => !key.includes('_ID') && !uneditableFields.includes(key),
          ),
        );
      }
    }
  }, [area]);

  useEffect(() => {
    setNewValue('');
  }, [selectedField]);

  const getLabel = (item: any) => {
    if (area === 'shutdowns') {
      const date = new Date(item.SHUTDOWN_DATE.value * 1000);
      const year = date.getFullYear();
      return item[name].value + ' - ' + year.toString();
    }
    if (area === 'operations') {
      return item[name].value + ' - ' + item['PROCESS'].value;
    }
    return item[name].value;
  };

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  //TODO:  Add options with label and value for specific properties in param and show a dropdown as new value
  //TODO: Add a yes/no for scrap rework and other boolean fields
  const totalPages = Math.ceil(
    (filteredData.allIds?.length ?? 0) / ELEMENTSPERPAGE,
  );
  return (
    <>
      <Button
        type="primaryOutline"
        onClick={() => setOpen(true)}
        disabled={!data.allIds || data.allIds.length === 0}
        icon={<Icon icon="IconEdit" color="#8A00E5" />}
      >
        Edit Multiple
      </Button>
      {open && data.allIds && data.allIds.length > 0 && (
        <Modal
          open={open}
          onClose={() => {
            setOpen(false);
            setStep(1);
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 600,
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              maxHeight: '80vh',
              overflowY: 'auto',
            }}
          >
            <Typography variant="h6" gutterBottom>
              Multi-Edit Modal
            </Typography>
            {step === 1 && (
              <>
                <Typography variant="subtitle1">
                  Step 1: Select Elements
                </Typography>
                <div style={{ marginBlock: 20 }}>
                  <InputFilter
                    id="Filter"
                    onChange={handleInputChangeDebounced}
                    placeholder="Filter"
                  />
                </div>
                <List>
                  {currentElements &&
                    currentElements.allIds.map((item) => (
                      <ListItem key={item} disablePadding>
                        <FormControlLabel
                          key={item}
                          control={
                            <Checkbox
                              checked={selectedElements.includes(item)}
                              onChange={() => handleToggle(item)}
                            />
                          }
                          label={getLabel(currentElements.byId[item])}
                        />
                      </ListItem>
                    ))}
                </List>
                <Box
                  sx={{
                    position: 'sticky',
                    padding: '0 16px',
                    left: 0,
                    bottom: 0,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    justifyContent: 'space-between',
                    bgcolor: 'background.paper',
                    py: 2,
                    zIndex: 2,
                    boxShadow: 3,
                    borderRadius: '8px',
                  }}
                >
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    {selectedElements.length > 0 ? (
                      <Button
                        type="primaryOutline"
                        onClick={() => setSelectedElements([])}
                      >
                        Deselect All
                      </Button>
                    ) : (
                      <Button
                        type="primaryOutline"
                        onClick={() => setSelectedElements(filteredData.allIds)}
                      >
                        Select All
                      </Button>
                    )}
                    <Button
                      type="primary"
                      disabled={selectedElements.length === 0}
                      onClick={handleNext}
                    >
                      Next
                    </Button>
                  </Box>
                  <div
                    style={{
                      display: 'flex',
                      gap: 10,
                      justifyContent: 'center',
                    }}
                  >
                    <Button
                      type="secondary"
                      size="small"
                      disabled={currentPage === 0}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      Previous
                    </Button>
                    <span style={{ alignSelf: 'center' }}>
                      Page {currentPage + 1} of {totalPages}
                    </span>
                    <Button
                      type="secondary"
                      size="small"
                      disabled={currentPage >= totalPages - 1}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </Box>
              </>
            )}
            {step === 2 && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  Step 2: Choose Field and Value
                </Typography>
                <Typography variant="subtitle1" sx={{ mt: 2 }} color={'red'}>
                  Are you sure you want to change these elements?
                  <br />
                  Incorrect changes might break the simulation!
                </Typography>
                <TextField
                  select
                  label="Field"
                  value={selectedField}
                  onChange={(e) => setSelectedField(e.target.value)}
                  fullWidth
                  margin="normal"
                >
                  {fieldsOptions.map((field) => (
                    <MenuItem key={field} value={field}>
                      {parsePropertytoLabel(field)}
                    </MenuItem>
                  ))}
                </TextField>
                {selectedField &&
                  (additionalOptions && additionalOptions[selectedField] ? (
                    <Select
                      value={newValue}
                      onChange={(e) => setNewValue(e.target.value)}
                      fullWidth
                    >
                      {additionalOptions[selectedField].map(
                        ({ label, value }) => (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        ),
                      )}
                    </Select>
                  ) : (
                    <TextField
                      label={
                        (selectedField.includes('TIME') &&
                          selectedField !== 'REDUCED_TIME') ||
                        selectedField.includes('DATE')
                          ? ''
                          : 'New Value'
                      }
                      value={newValue}
                      onChange={(e) => setNewValue(e.target.value)}
                      fullWidth
                      margin="normal"
                      type={
                        selectedField.includes('DATE')
                          ? 'date'
                          : selectedField.includes('TIME') &&
                            selectedField !== 'REDUCED_TIME'
                          ? 'time'
                          : typeof filteredData.byId[selectedElements[0]][
                              selectedField
                            ].value
                      }
                    />
                  ))}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    mt: 2,
                    justifyContent: 'space-between',
                  }}
                >
                  <Button type="primaryOutline" onClick={handleBack}>
                    Back
                  </Button>
                  <Button type="primary" onClick={handleSubmit}>
                    Apply Changes
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Modal>
      )}
    </>
  );
};

export default MultiEditModal;
